import { useEffect, useMemo, useState, type MutableRefObject, type ReactNode } from 'react';
import { cn, MicroLabel } from '../ui/primitives';
import type { ClientLayoutSection } from '../types';

/**
 * The client's sections, in one column with an index rail beside them.
 *
 * ── Why a rail and not the tabs this page had ──
 *
 * The v1 page was tabs, and they cost more than they saved. A tab hides its
 * siblings, so "this client has three open quotes and an order in production"
 * took three clicks to learn; the rail shows all four counts at once, which is
 * the actual question someone opens a client to answer.
 *
 * They also made the brand kit unreachable on reload: the open brand lived in
 * tab state, so a refresh dropped you back on the list. In this layout a brand
 * is a card that links to its own page, and the problem stops existing rather
 * than needing a query param to paper over it.
 *
 * `@codezo/quote-detail` keeps tabs, and correctly: a quote is a set of
 * separate jobs and only one is worked at a time. A client account is the
 * opposite — it is one record read top to bottom.
 *
 * ── Where the active state comes from ──
 *
 * From the scroll position, via IntersectionObserver, NOT from clicks. A rail
 * that only updates on click lies the moment someone scrolls.
 */

export function sectionAnchorId(id: string) {
	return `client-sec-${id}`;
}

export function scrollToSection(id: string) {
	document.getElementById(sectionAnchorId(id))?.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	});
}

export function ClientSections({
	sections,
	railLabel = 'Client',
	railFooter,
	revealRef,
	className,
}: {
	sections: ClientLayoutSection[];
	/** The rail's own heading. */
	railLabel?: string;
	/**
	 * Anything that belongs under the index — the brand filter, a line of
	 * account trivia. A slot rather than a prop because what goes there is
	 * app-specific and interactive, and the rail should not grow a second job.
	 */
	railFooter?: ReactNode;
	/**
	 * Filled with "take me to this section", for anything outside the sections
	 * that points at one — an overview metric that jumps to what it counts.
	 *
	 * A ref rather than context because the things that need it are props
	 * evaluated in the caller, outside any provider this could wrap around its
	 * own children. Same reasoning as the brand and quote packages'.
	 */
	revealRef?: MutableRefObject<((sectionId: string) => void) | null>;
	className?: string;
}) {
	const shown = useMemo(() => sections.filter((s) => !s.hidden), [sections]);
	const [active, setActive] = useState(() => shown[0]?.id ?? '');

	/* The list moves with the data — a client with no brands loses that
	   section. If the active id goes with it, fall to the first rather than
	   highlighting nothing. */
	const idKey = shown.map((s) => s.id).join('|');
	useEffect(() => {
		const ids = idKey ? idKey.split('|') : [];
		if (ids.length > 0 && !ids.includes(active)) setActive(ids[0]);
	}, [idKey, active]);

	useEffect(() => {
		const ids = idKey ? idKey.split('|') : [];
		if (ids.length === 0) return;

		/* The band is deliberately narrow and high on the page: it marks the
		   section whose top has just passed under the header, which is the one
		   a reader considers themselves to be in. A full-height root would
		   report every section the tall ones overlap. */
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setActive(entry.target.id.replace('client-sec-', ''));
					}
				}
			},
			{ rootMargin: '-15% 0px -75% 0px' }
		);

		for (const id of ids) {
			const el = document.getElementById(sectionAnchorId(id));
			if (el) observer.observe(el);
		}
		return () => observer.disconnect();
	}, [idKey]);

	useEffect(() => {
		if (!revealRef) return;
		revealRef.current = scrollToSection;
		return () => {
			revealRef.current = null;
		};
	}, [revealRef]);

	return (
		<div className={cn('grid gap-8 lg:grid-cols-[12rem_minmax(0,1fr)]', className)}>
			<nav className="hidden lg:block">
				<div className="sticky top-5">
					<MicroLabel className="block px-2.5 pb-2">{railLabel}</MicroLabel>
					{shown.map((section) => {
						const on = active === section.id;
						return (
							<button
								key={section.id}
								type="button"
								onClick={() => scrollToSection(section.id)}
								className={cn(
									'relative flex w-full items-center gap-2.5 rounded-[var(--r-2)] px-2.5 py-1.5 text-left text-xs transition-colors duration-[var(--dur-fast)]',
									on
										? 'bg-surface-fill text-text-1'
										: 'bg-transparent text-text-3 hover:text-text-1'
								)}
							>
								{on && (
									<span className="absolute -left-2 top-1/4 bottom-1/4 w-0.5 rounded-full bg-accent" />
								)}
								<span className="truncate">{section.label}</span>
								{section.count !== undefined && (
									<span className="ml-auto font-mono text-2xs text-text-4">
										{section.count}
									</span>
								)}
							</button>
						);
					})}
					{railFooter && (
						<div className="mt-3.5 border-t border-line-1 pt-3.5">{railFooter}</div>
					)}
				</div>
			</nav>

			<div className="flex min-w-0 flex-col gap-11">
				{shown.map((section) => (
					<section
						key={section.id}
						id={sectionAnchorId(section.id)}
						/* The rail's own sticky offset, so a click lands the
						   heading below the header rather than under it. */
						className="scroll-mt-5"
					>
						{section.content}
					</section>
				))}
			</div>
		</div>
	);
}
